/**
 * HopeConvaiEmbed - Convai Experience Embed with Pixel Streaming
 *
 * Displays YOUR Hope METAHUMAN character from Convai with lip-sync
 * This uses the experience_id to load the correct character
 */

import React, { useRef, useEffect } from 'react';
import { PixelStreamComponent, PixelStreamComponentHandles } from '@convai/experience-embed';

interface HopeConvaiEmbedProps {
  isTalking?: boolean;
  isListening?: boolean;
}

export default function HopeConvaiEmbed({
  isTalking = false,
  isListening = false,
}: HopeConvaiEmbedProps) {
  const pixelStreamRef = useRef<PixelStreamComponentHandles>(null);

  // Hope's experience ID from Convai Publish > Embed Experience (the "Meet Hope" character)
  const HOPE_EXPERIENCE_ID = 'aabd0c48-4938-4cab-ad81-8b4ebd4fa79c';
  const HOPE_BG_IMAGE = 'https://storage.googleapis.com/experience-asset-storage/user-uploaded-exp-assets/aabd0c48-4938-4cab-ad81-8b4ebd4fa79c_experience_thumbnail';

  // Auto-start the experience when component mounts (skip "Click to start" screen)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pixelStreamRef.current?.initializeExperience) {
        try {
          pixelStreamRef.current.initializeExperience();
          console.log('[HopeConvaiEmbed] Auto-started experience');
        } catch (err) {
          console.log('[HopeConvaiEmbed] Auto-start not available, user click required');
        }
      }
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div style={styles.container}>
      {/* Cropped container — hides Convai's built-in chat input, mic, watermark, and speech bubbles.
          These are inside a cross-origin iframe so CSS can't reach them.
          Instead we crop: cut bottom 15% (text input + mic + watermark) and top 10% (camera icon). */}
      <div style={styles.cropWrapper}>
        <div style={styles.scaledInner}>
          <PixelStreamComponent
            ref={pixelStreamRef}
            expId={HOPE_EXPERIENCE_ID}
          />
        </div>
      </div>

      {/* Block the speech bubble area (bottom-center above the crop line) */}
      <div style={styles.bubbleBlocker} />
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    width: '100%',
    height: '100%',
    position: 'relative',
    backgroundColor: '#1a1a2e',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  // Crop wrapper: clips top 8% (camera icon) and bottom 15% (chat input + mic + watermark)
  cropWrapper: {
    position: 'absolute',
    top: '-8%',
    left: 0,
    right: 0,
    bottom: '-15%',
    overflow: 'hidden',
  },
  // Scale the embed up slightly to compensate for the crop
  scaledInner: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  // Covers the speech bubble area at the bottom of the visible region
  bubbleBlocker: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '60px',
    background: 'linear-gradient(transparent, rgba(26, 26, 46, 0.95))',
    zIndex: 2,
    pointerEvents: 'none',
  },
};
